import React from 'react';
import {
  Await,
  redirect,
  useFetcher,
  useLoaderData,
  type LoaderFunctionArgs
} from 'react-router-dom';

import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@oysterjs/ui/dialog';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';

import { client } from '~/api/client';
import { loadMailboxAccounts, loadMailboxProviders } from '~/api/data/mailbox/queries';
import {
  Action,
  Resource,
  Scope,
  MailboxSettingsAccessDocument,
  type MailboxSettingsAccessQuery,
  type MailboxSettingsAccessQueryVariables,
  MailboxOffboardingAccountsDocument,
  type MailboxOffboardingAccountsQuery,
  type MailboxOffboardingAccountsQueryVariables,
  type DisconnectMailboxMutation,
  SubscriptionEntitlement
} from '~/api/graphql';
import { FEATURE_FLAGS } from '~/components/feature-flags';
import { MailboxUpgradeNotice } from '~/components/mailbox/mailbox-guard';
import { SettingsMailboxPage } from './mailbox';

type OffboardingAccount = MailboxOffboardingAccountsQuery['mailboxOffboardingAccounts'][number];

// Route loaders run before element guards. Decide from a fresh authenticated
// snapshot before starting *any* mailbox read; the root loader may run in parallel.
export const loadMailboxSettings = async (args: LoaderFunctionArgs) => {
  const { data } = await client.query<
    MailboxSettingsAccessQuery,
    MailboxSettingsAccessQueryVariables
  >({
    query: MailboxSettingsAccessDocument,
    fetchPolicy: 'network-only',
    errorPolicy: 'none'
  });
  const user = data?.agencyUser;
  if (!user) throw redirect('/app/signin');
  if (!user.agency.entitlements.includes(SubscriptionEntitlement.Mailboxes)) {
    return { mode: 'upgrade' as const };
  }
  if (user.frontendFlags.includes(FEATURE_FLAGS.MAILBOX)) {
    return {
      mode: 'enabled' as const,
      data: Promise.all([loadMailboxAccounts(args), loadMailboxProviders(args)]).then(
        ([mailboxAccounts, mailboxProviders]) => ({ mailboxAccounts, mailboxProviders })
      )
    };
  }
  const permissions = user.permissions ?? [];
  const canDelete = permissions.some(
    (p) => p.resource === Resource.Mailbox && p.action === Action.Delete
  );
  const canList = permissions.some(
    (p) => p.resource === Resource.Mailbox && p.action === Action.List
  );
  const canOffboardById = permissions.some(
    (p) =>
      p.resource === Resource.Mailbox &&
      p.action === Action.Delete &&
      (p.scope === Scope.Organization || p.scope === Scope.Global)
  );
  return {
    mode: 'offboarding' as const,
    canOffboardById,
    data:
      canDelete && canList
        ? client
            .query<MailboxOffboardingAccountsQuery, MailboxOffboardingAccountsQueryVariables>({
              query: MailboxOffboardingAccountsDocument,
              fetchPolicy: 'network-only',
              errorPolicy: 'none'
            })
            .then(({ data }) => {
              // Absence is not a successful empty read.
              if (!data?.mailboxOffboardingAccounts)
                throw new Error('Could not read mailbox offboarding choices.');
              return { accounts: data.mailboxOffboardingAccounts, failed: false };
            })
            // This promise may settle before the route mounts. Handle rejection
            // here, retaining a distinct failure state rather than an empty list.
            .catch(() => ({ accounts: [] as OffboardingAccount[], failed: true }))
        : Promise.resolve({ accounts: [] as OffboardingAccount[], failed: false })
  };
};

type SettingsData = Awaited<ReturnType<typeof loadMailboxSettings>>;

export const MailboxSettingsEntry = () => {
  const loader = useLoaderData() as SettingsData;
  if (loader.mode === 'upgrade') return <MailboxUpgradeNotice />;
  if (loader.mode === 'enabled') return <SettingsMailboxPage />;
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Disconnect mailboxes</h2>
      <p>
        Mailbox features are currently unavailable. You can still disconnect an existing mailbox.
      </p>
      <React.Suspense fallback={<p>Loading disconnectable mailboxes…</p>}>
        <Await
          resolve={loader.data}
          errorElement={<p role="alert">Could not load disconnectable mailboxes.</p>}
        >
          {({ accounts, failed }) =>
            failed ? (
              <p role="alert">Could not load disconnectable mailboxes.</p>
            ) : (
              <OffboardingChoices accounts={accounts} canOffboardById={loader.canOffboardById} />
            )
          }
        </Await>
      </React.Suspense>
    </section>
  );
};

const OffboardingChoices = ({
  accounts,
  canOffboardById
}: {
  accounts: OffboardingAccount[];
  canOffboardById: boolean;
}) => {
  const [knownId, setKnownId] = React.useState('');
  const [target, setTarget] = React.useState<{ id: string; label: string } | null>(null);
  return (
    <div className="space-y-4">
      {accounts.length === 0 ? (
        <p>No readable mailboxes are available to disconnect.</p>
      ) : (
        <ul className="space-y-3">
          {accounts.map((account) => (
            <li key={account.id} className="flex items-center justify-between gap-4">
              <span>{account.emailAddress}</span>
              <Button
                outline
                onClick={() => setTarget({ id: account.id, label: account.emailAddress })}
              >
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      )}
      {canOffboardById ? (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            const id = knownId.trim();
            if (id) setTarget({ id, label: `account ID ${id}` });
          }}
        >
          <Field>
            <Label>Known mailbox account ID</Label>
            <Input value={knownId} onChange={(event) => setKnownId(event.target.value)} required />
          </Field>
          <p>
            For agency offboarding only. Enter an ID you already know. No account lookup is
            performed.
          </p>
          <Button type="submit" outline disabled={!knownId.trim()}>
            Review disconnect by ID
          </Button>
        </form>
      ) : null}
      {target ? (
        <OffboardingConfirmation key={target.id} target={target} onClose={() => setTarget(null)} />
      ) : null}
    </div>
  );
};

const OffboardingConfirmation = ({
  target,
  onClose
}: {
  target: { id: string; label: string };
  onClose: () => void;
}) => {
  const fetcher = useFetcher();
  const result = fetcher.data as ActionResult<DisconnectMailboxMutation>;
  const submitting = fetcher.state !== 'idle';
  const failure =
    result?.applicationError?.message ??
    (result?.serverError ? 'Could not disconnect the mailbox.' : null) ??
    (result && result.data?.disconnectMailbox !== true
      ? 'The mailbox was not disconnected.'
      : null);
  React.useEffect(() => {
    if (!submitting && result?.data?.disconnectMailbox === true && !failure) onClose();
  }, [submitting, result, failure, onClose]);
  return (
    <Dialog
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
    >
      <DialogTitle>Disconnect mailbox</DialogTitle>
      <DialogDescription>
        Disconnect {target.label}? Cara will forget its sign-in and stop using this mailbox,
        including for shared users. Previously sent email remains in the send log.
      </DialogDescription>
      {failure ? <p role="alert">{failure}</p> : null}
      <DialogActions>
        <Button outline disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="red"
          loading={submitting}
          onClick={() =>
            fetcher.submit(
              { id: target.id },
              {
                method: 'POST',
                action: '/app/settings/mailbox/disconnect',
                encType: 'application/json'
              }
            )
          }
        >
          Confirm disconnect
        </Button>
      </DialogActions>
    </Dialog>
  );
};
